import React, { useState, useEffect } from "react";
import { instanceOf } from "prop-types";
import { withCookies, Cookies } from "react-cookie";
import { pick } from "lodash";
import 'react-date-picker/dist/DatePicker.css';
import "./App.css";

import Authentication from "./components/Logic/Authentication";
import PublicLogin from "./components/PublicLogin";
import Footer from "./components/Footer";
import POSLoading from "./components/POSLoading";

import { NotificationContainer } from "react-notifications";

import isAbsoluteURL from "axios/lib/helpers/isAbsoluteURL";
import buildURL from "axios/lib/helpers/buildURL";
import combineURLs from "axios/lib/helpers/combineURLs";

import UserContext from "./components/context/userContext";
import { getCookie, setCookie, clearAuthData } from "./services/axiosInstance";
import siteService from "./services/site";
import posService from "./services/pos";
import empresasService from "./services/empresas";
import loginService from "./services/login";

// EventBus import removed - no longer needed in this file

// Function to validate session using login middleware
async function validateSession() {
  try {
    // Use the login service with middleware for proper token handling
    // Token is automatically added by axiosInstance middleware
    const data = await loginService.validateSession();
    
    if (data && data.valid && data.cookies) {
      // Check if important cookies have changed
      const cookiesData = data.cookies;
      const currentMemberIdempresa = getCookie('member_idempresa');
      const currentMemberType = getCookie('member_type');
      const currentMemberIdemployee = getCookie('member_idemployee');
      const cookiesChanged = (
        (cookiesData.member_idempresa && cookiesData.member_idempresa !== currentMemberIdempresa) ||
        (cookiesData.member_type && cookiesData.member_type !== currentMemberType) ||
        (cookiesData.member_idemployee && cookiesData.member_idemployee !== currentMemberIdemployee)
      );
      
      // Set all cookies from response
      if (cookiesData.token) setCookie('token', cookiesData.token, 1);
      if (cookiesData.token_js) setCookie('token_js', cookiesData.token_js, 1);
      if (cookiesData.ePOS) setCookie('ePOS', cookiesData.ePOS, 1);
      if (cookiesData.idempresa) setCookie('idempresa', cookiesData.idempresa, 1);
      if (cookiesData.session_token) setCookie('session_token', cookiesData.session_token, 1);
      
      // Set all member_* cookies
      if (cookiesData.member_idempresa) setCookie('member_idempresa', cookiesData.member_idempresa, 1);
      if (cookiesData.member_idemployee) setCookie('member_idemployee', cookiesData.member_idemployee, 1);
      if (cookiesData.member_idempresa_sec) setCookie('member_idempresa_sec', cookiesData.member_idempresa_sec, 1);
      if (cookiesData.member_type) setCookie('member_type', cookiesData.member_type, 1);
      if (cookiesData.member_name) setCookie('member_name', cookiesData.member_name, 1);
      if (cookiesData.member_rname) setCookie('member_rname', cookiesData.member_rname, 1);
      if (cookiesData.member_avatar) setCookie('member_avatar', cookiesData.member_avatar, 1);
      if (cookiesData.member_nif) setCookie('member_nif', cookiesData.member_nif, 1);
      if (cookiesData.combustivel) setCookie('combustivel', cookiesData.combustivel, 1);
      
      // Update localStorage as well for backward compatibility
      if (data.token) localStorage.setItem('token', data.token);
      if (data.idempresa) localStorage.setItem('public_indentifier', data.idempresa);
      if (data.avatar) localStorage.setItem('avatar', data.avatar);
      
      // Only reload if important cookies changed
      if (cookiesChanged) {
        console.log('[App] Important cookies changed - reloading page');
        window.location.reload();
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Session validation error:', error);
    return false;
  }
}

function App({ cookies }) {
  const profile = pick(cookies.getAll(), [
    "member_idempresa",
    "member_idempresa_sec",
    "member_type",
    "combustivel",
    "page",
    "member_idcliente",
    "demo",
    "prizes",
    "pontos",
    "member_avatar",
    "company_avatar",
    "member_rname",
    "member_name",
    "member_idemployee",
  ]);

  const isPublicPage = window.location.href.includes("/public");

  if ((!isPublicPage) && (!profile["member_idempresa"] || !profile["member_type"])) {
    window.location.href = `https://eposgestofinal.com?redirect=${window.location.href}`;
  }

  const combustivel = parseInt(profile["combustivel"], 10) === 1;
  const memberType = parseInt(profile["member_type"], 10);
  const idEmpresa = parseInt(profile["member_idempresa"], 10);
  const idEmpresaSec = memberType > 9 ? (profile["member_idempresa_sec"] ? parseInt(profile["member_idempresa_sec"], 10) : idEmpresa) : idEmpresa;
  const idEmployee = parseInt(profile["member_idemployee"], 10);
  const name = profile["member_rname"] && profile["member_rname"].split("+").join(" ");
  const avatar = profile["member_avatar"];
  const companyAvatar = profile["member_avatar"];
  // Use cookies as primary source, localStorage as fallback
  // This will be updated when fullEmpresa is loaded
  const identifier = getCookie('idempresa') || localStorage.getItem("public_indentifier");
  // Note: 'token' cookie is httpOnly, so we use 'token_js' instead
  const token = getCookie('token_js') || getCookie('token') || localStorage.getItem("token");
  const loggedIn = identifier && token;

  const [ready, setReady] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [fullName, setFullName] = useState('');
  const [fullEmpresa, setEmpresa] = useState({});

  useEffect(() => {
    let isMounted = true;
    
    const loadAppData = async () => {
      try {
        // Validate session first if we have a token (for refresh scenarios)
        if (token && isPublicPage) {
          try {
            const sessionValid = await validateSession();
            if (!sessionValid) {
              // Session is invalid, use middleware function to clear auth data
              clearAuthData();
              if (isMounted) {
                setReady(true);
              }
              return;
            }
          } catch (error) {
            // If validation fails (401, network error, etc.), clear auth data
            clearAuthData();
            if (isMounted) {
              setReady(true);
            }
            return;
          }
        }
        
        // Load permissions and empresa data in parallel
        const [sitePermissions, posPermissions, empresa] = await Promise.all([
          siteService.getPermissions(idEmpresa),
          posService.getPermissions(idEmpresa),
          empresasService.getById(idEmpresa)
        ]);

        if (!isMounted) return;

        // Set permissions
        const newPermissions = [
          ...Object.keys(posPermissions)
            .filter((permission) => posPermissions[permission] === "t")
            .map((permission) => `pos:${permission}`),
          ...Object.keys(sitePermissions)
            .filter((permission) => sitePermissions[permission] === "t")
            .map((permission) => `site.${permission}`),
        ];
        setPermissions(newPermissions);

        // Set empresa data
        if (empresa) {
          setFullName(empresa.name);
          setEmpresa(empresa);
        }

        setReady(true);
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setReady(true);
        }
      }
    };

    loadAppData();
    
    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adminProfile = memberType === 10;

  const URL_KEY = "__AXIOS-DEBUG-LOG_URL__";

  const getUrl = (config) => {
    const url =
      config.baseURL && !isAbsoluteURL(config.url)
        ? combineURLs(config.baseURL, config.url)
        : config.url;

    return buildURL(url, config.params, config.paramsSerializer);
  };

  const getUrlAndMethod = (config) => {
    const url = config[URL_KEY] || getUrl(config);
    const method = config.method.toUpperCase();

    return {
      url,
      method,
    };
  };

  require("axios-debug-log")({
    request: (_, config) => {
      const { url, method } = getUrlAndMethod(config);

      Object.defineProperty(config, URL_KEY, { value: url });

      console.info(`${method} ${url}`);
    },
    response: (_, response) => {
      const { url, method } = getUrlAndMethod(response.config);
      const { status, statusText } = response;

      console.info(`${status} ${statusText} (${method} ${url})`);
    },
    error: (_, error) => {
      const { name, message } = error;

      if (error.config) {
        const { url, method } = getUrlAndMethod(error.config);

        console.error(`${name}: ${message} (${method} ${url})`);
      } else {
        console.error(`${name}: ${message}`);
      }
    },
  });

  console.log(permissions);

  // Determine the correct company public avatar with proper priority
  const companyPublicAvatar = fullEmpresa?.avatar || getCookie('avatar') || localStorage.getItem("avatar");

  return ready ? (
    <UserContext.Provider
      value={{
        ...profile,
        fullName,
        fullEmpresa,
        name,
        avatar,
        companyAvatar,
        companyPublicAvatar,
        memberType,
        combustivel,
        idEmpresa,
        idEmpresaSec,
        idEmployee,
        adminProfile,
        permissions,
        identifier,
        token,
      }}
    >
      <POSLoading />
      <div className="App">
        {isPublicPage && !loggedIn && (
          <PublicLogin />
        )}
        {((isPublicPage && loggedIn) || !isPublicPage) &&
          <>
            <Authentication />
          </>
        }
        <NotificationContainer />
        <Footer />
      </div>
    </UserContext.Provider>
  ) : null;
}

App.propTypes = {
  cookies: instanceOf(Cookies).isRequired,
};

export default withCookies(App);
